// Whole-page states. None of them is ever empty or reassuring: until an answer arrives, and whenever the
// answer cannot be had, the page says in words that nothing on it is known.
import type { Look } from "./present";
import { POLL_MS, SKEW_TOLERANCE_MS, STALE_AFTER_MS, fmtAgoMs, fmtUtc } from "./present";
import { StateWord } from "./Tiles";

const UNKNOWN_LOOK: Look = { kind: "unknown", word: "UNKNOWN", contradiction: null };
const SIGN_IN_HREF = "/.auth/login/aad?post_login_redirect_uri=%2Fhealth";

export function Loading() {
  return (
    <section className="ph-status ph-k-unknown" data-testid="health-loading">
      <p className="ph-status-word">
        <StateWord lk={UNKNOWN_LOOK} />
      </p>
      <h2>Fetching platform health…</h2>
      <p>Nothing on this page is known until the first answer arrives.</p>
    </section>
  );
}

export function NotSignedIn() {
  return (
    <section className="ph-status ph-status-plain" data-testid="health-not-signed-in">
      <h2>Sign in to see platform health</h2>
      <p>
        The health API answered that this browser is not signed in, or that the session has expired. Nothing on this page is known until
        you sign in.
      </p>
      <p>
        <a className="ph-signin" href={SIGN_IN_HREF} data-testid="health-sign-in">
          Sign in with Microsoft
        </a>
      </p>
    </section>
  );
}

export function Forbidden() {
  return (
    <section className="ph-status ph-status-plain" data-testid="health-forbidden">
      <h2>This page is restricted to portal administrators.</h2>
    </section>
  );
}

export function NothingKnown({ why, lastSuccessMs, now }: { why: string; lastSuccessMs: number | null; now: number }) {
  return (
    <section className="ph-status ph-fail ph-k-unknown" role="alert" data-testid="health-unknown-banner">
      <p className="ph-status-word">
        <StateWord lk={UNKNOWN_LOOK} />
      </p>
      <h2>Nothing on this page is known.</h2>
      <p className="ph-reason">{why}</p>
      <p>
        Last successful fetch:{" "}
        {lastSuccessMs !== null ? `${fmtUtc(new Date(lastSuccessMs).toISOString())} (${fmtAgoMs(now - lastSuccessMs)})` : "never"}. The page
        retries every {POLL_MS / 60_000} minutes. Nothing from an earlier answer is shown, because it can no longer be vouched for.
      </p>
      <p>This is not a statement that the platform is healthy or unhealthy. It is a statement that this page cannot see it.</p>
    </section>
  );
}

/** The answer on screen has aged past three polls, or claims a time ahead of this browser's clock. */
export function StaleBanner({ evaluatedAt, now }: { evaluatedAt: string; now: number }) {
  const age = now - Date.parse(evaluatedAt);
  if (age < -SKEW_TOLERANCE_MS) {
    return (
      <p className="ph-banner ph-k-unknown" role="alert" data-testid="stale-banner">
        <StateWord lk={UNKNOWN_LOOK} /> This answer says it was evaluated {fmtAgoMs(age)}: the server's clock and this browser's disagree, so
        the ages on this page cannot be trusted.
      </p>
    );
  }
  if (age <= STALE_AFTER_MS) return null;
  return (
    <p className="ph-banner ph-k-unknown" role="alert" data-testid="stale-banner">
      <StateWord lk={UNKNOWN_LOOK} /> This answer was evaluated {fmtAgoMs(age)} and has not been refreshed since (the page refreshes every{" "}
      {POLL_MS / 60_000} minutes). Treat everything below as UNKNOWN until it is.
    </p>
  );
}

export function StoreUnreadableBanner({ error }: { error: string | null }) {
  return (
    <p className="ph-banner ph-k-unknown" role="alert" data-testid="store-unreadable-banner">
      <StateWord lk={UNKNOWN_LOOK} /> The state store could not be read{error ? ` (${error})` : ""} - nothing on this page is known.
    </p>
  );
}
